import { formatCount, formatTimestamp, statusLabel } from '@saay/formatting';
import type { AdminSelf } from '@saay/api-client';
import type { components } from '@saay/api-client';
import type { AdminBuyerSummary } from '../api.js';
import { IconTile, StatusBadge, type Tone } from '@saay/blocks';
import { buttonVariants } from '@saay/shadcn/components/button';
import { physicalCardsPath } from '../nav.js';
import { ErrorState, Money, Masked, PageHeader, useSearchInput, useWindowClass } from '../kit.js';
import { PlusIcon, PrinterIcon, UserRoundIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate, useSearchParams } from 'react-router';

import { AdminListTable, type DataTableRowAction } from '../components/admin-list-table.js';
import { buyerSearchFromText } from '../buyer-search.js';
import { useShellCenterAction } from '../components/admin-shell.js';
import { QueryState, RequireArea, usePageTitle } from '../components/guard.js';
import { ListFooter, ListLoadMore, totalEyebrow } from '../components/list-footer.js';
import { FilterPill, FilterPillBar } from '../components/filter-pills.js';
import { ListSearchInput } from '../components/list-filters.js';
import { ClearFiltersButton, ListEmpty, ListSkeleton } from '../components/list-state.js';
import { OPENED_FROM_LIST } from '../components/route-sheet.js';
import { OUTLINE_PILL, SurfaceSection } from '../components/surface-section.js';
import { readBuyersTableColumns, storeBuyersTableColumns } from '../device-prefs.js';
import { canPerform, canPrintPhysicalCards, canUse, hasCapability } from '../permissions.js';
import {
  type BuyerFilters,
  firstPageTotal,
  listStatus,
  useBuyers,
  useBuyersSummary,
} from '../queries.js';

/** The v2 tone of a buyer status (D-93): active olive, suspended copper, closed neutral. */
const BUYER_STATUS_TONE: Readonly<Record<string, Tone>> = {
  ACTIVE: 'olive',
  CLOSED: 'forest',
  SUSPENDED: 'copper',
};

function BuyerStatusBadge({ value }: { readonly value: string }) {
  return (
    <StatusBadge tone={BUYER_STATUS_TONE[value] ?? 'forest'}>
      {statusLabel('buyerStatus', value)}
    </StatusBadge>
  );
}

const FILTER_KEYS = ['status', 'q', 'mobileE164', 'nationalCode'] as const;
type FilterKey = (typeof FILTER_KEYS)[number];

type BuyerCounts = components['schemas']['AdminBuyersSummary']['buyerCountsByStatus'];

const BUYER_STATUS_FILTER_OPTIONS = [
  { key: 'all', status: '' },
  { key: 'ACTIVE', status: 'ACTIVE' },
  { key: 'PENDING_VERIFICATION', status: 'PENDING_VERIFICATION' },
  { key: 'SUSPENDED', status: 'SUSPENDED' },
  { key: 'CLOSED', status: 'CLOSED' },
] as const;

function buyerStatusOptions(counts?: BuyerCounts) {
  return BUYER_STATUS_FILTER_OPTIONS.filter((option) => option.key !== 'all').flatMap((option) => {
    const count = counts?.[option.key as keyof BuyerCounts];
    if (count === 0) return [];
    return [
      {
        label: `${statusLabel('buyerStatus', option.key)}${count === undefined ? '' : ` ${formatCount(count)}`}`,
        value: option.status,
      },
    ];
  });
}

export interface BuyersOutletContext {
  readonly self: AdminSelf;
}

/** Opens a buyer sheet action from the list row menu (ADM-37). */
export const BUYER_QUICK_ACTION_KEY = 'buyerQuickAction';

export type BuyerListQuickAction = 'identity' | 'login-lock' | 'physical-card' | 'suspend';

function buyerRowActions(
  row: AdminBuyerSummary,
  self: AdminSelf,
  navigate: ReturnType<typeof useNavigate>,
  search: string,
): readonly DataTableRowAction<AdminBuyerSummary>[] {
  const open = (quick?: BuyerListQuickAction) => {
    navigate(
      { pathname: `/buyers/${row.buyerId}`, search },
      {
        state: {
          ...OPENED_FROM_LIST,
          ...(quick === undefined ? {} : { [BUYER_QUICK_ACTION_KEY]: quick }),
        },
      },
    );
  };
  const actions: DataTableRowAction<AdminBuyerSummary>[] = [];
  if (hasCapability(self, 'BUYER_PHYSICAL_CARD_MANAGE')) {
    actions.push({ label: 'مسدودسازی کارت', onSelect: () => open('physical-card') });
  }
  if (row.status === 'PENDING_VERIFICATION' && canUse(self, 'verifyBuyerIdentity')) {
    actions.push({ label: 'بررسی مدرک', onSelect: () => open('identity') });
  }
  if (canUse(self, 'requestLoginLockClearance')) {
    actions.push({ label: 'رفع قفل ورود', onSelect: () => open('login-lock') });
  }
  if (
    row.status !== 'CLOSED' &&
    canUse(self, 'transitionBuyerStatus') &&
    (row.status === 'ACTIVE' || row.status === 'SUSPENDED')
  ) {
    const suspend = row.status === 'ACTIVE';
    actions.push({
      destructive: suspend,
      label: suspend ? 'تعلیق' : 'رفع تعلیق',
      onSelect: () => open('suspend'),
    });
  }
  return actions;
}

function readFilters(params: URLSearchParams): BuyerFilters {
  const filters: Record<string, string> = {};
  for (const key of FILTER_KEYS) {
    const value = params.get(key);
    if (value !== null && value !== '') {
      filters[key] = value;
    }
  }
  return filters as BuyerFilters;
}

function searchDisplayText(filters: BuyerFilters): string {
  return filters.q ?? filters.mobileE164 ?? filters.nationalCode ?? '';
}

function BuyerToolbar({
  counts,
  filters,
  onChange,
  onOpenSingle,
}: {
  readonly counts?: BuyerCounts;
  readonly filters: BuyerFilters;
  readonly onChange: (changes: Partial<Record<FilterKey, string>>, clearAll?: boolean) => void;
  readonly onOpenSingle: () => void;
}) {
  const search = useSearchInput({
    applied: searchDisplayText(filters),
    minLength: 0,
    onApply: () => undefined,
    invalidMessage: '',
  });
  const [message, setMessage] = useState<string | undefined>(undefined);

  function applySearch() {
    const parsed = buyerSearchFromText(search.text);
    if ('message' in parsed) {
      setMessage(parsed.message);
      return;
    }
    setMessage(undefined);
    onChange({
      nationalCode: '',
      mobileE164: '',
      q: '',
      ...parsed.filters,
    });
    onOpenSingle();
  }

  const searchMessage = message ?? search.message;
  return (
    <div>
      <FilterPillBar
        activeFilterCount={Object.keys(filters).length}
        label="فیلتر خریداران"
        onClearAll={() => onChange({}, true)}
      >
        <form
          className="shrink-0"
          onSubmit={(event) => {
            event.preventDefault();
            applySearch();
          }}
        >
          <ListSearchInput
            invalid={message !== undefined}
            label="جست‌وجوی خریدار"
            onChange={(event) => {
              setMessage(undefined);
              search.setText(event.target.value);
            }}
            placeholder="نام، موبایل یا کد ملی"
            value={search.text}
          />
        </form>
        <FilterPill
          emptyLabel="همه وضعیت‌ها"
          label="وضعیت خریدار"
          onChange={(status) => onChange({ status })}
          options={buyerStatusOptions(counts)}
          value={filters.status ?? ''}
        />
      </FilterPillBar>
      {searchMessage === undefined ? null : (
        <p className="mb-3 text-sm text-destructive">{searchMessage}</p>
      )}
    </div>
  );
}

export function BuyersPage() {
  // The list names the page only while no route sheet is open over it (the outlet is never null).
  const { pathname } = useLocation();
  usePageTitle('خریداران و اعتبار', pathname.replace(/\/$/, '') === '/buyers');
  return <RequireArea area="buyers">{(self) => <BuyersView self={self} />}</RequireArea>;
}

function isBuyerDetailPath(pathname: string): boolean {
  return /^\/buyers\/(?!new(?:\/|$))[^/]+$/.test(pathname);
}

function BuyersView({ self }: { readonly self: AdminSelf }) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { pathname, search } = useLocation();
  const detailPage = isBuyerDetailPath(pathname);
  const filters = readFilters(params);
  const buyers = useBuyers(filters);
  const summary = useBuyersSummary(canUse(self, 'getAdminBuyersSummary'));
  const rows = buyers.data?.pages.flatMap((page) => page.items) ?? [];
  const total = firstPageTotal(buyers.data);
  const filtered = Object.keys(filters).length > 0;
  const canProvision = canPerform(self, 'provisionBuyer');
  const canExportCards = canPrintPhysicalCards(self);
  const windowClass = useWindowClass('expanded');
  useShellCenterAction(
    !detailPage && canProvision && windowClass === 'compact'
      ? { href: '/buyers/new', label: 'خریدار جدید', state: OPENED_FROM_LIST }
      : null,
  );
  const open = (buyerId: string) => ({ pathname: `/buyers/${buyerId}`, search });
  const pendingExactOpen = useRef(false);
  const [visibleColumnIds, setVisibleColumnIds] = useState(readBuyersTableColumns);

  function change(changes: Partial<Record<FilterKey, string>>, clearAll = false) {
    const next = new URLSearchParams(clearAll ? '' : params);
    for (const key of FILTER_KEYS) {
      next.delete(key);
    }
    const merged = clearAll ? {} : { ...filters, ...changes };
    for (const [key, value] of Object.entries(merged)) {
      if (value !== undefined && value !== '') next.set(key, value);
    }
    setParams(next);
  }

  function tryOpenSingle() {
    pendingExactOpen.current = true;
  }

  useEffect(() => {
    if (!pendingExactOpen.current || buyers.isFetching || rows.length !== 1) {
      return;
    }
    pendingExactOpen.current = false;
    const row = rows[0];
    if (row === undefined) return;
    navigate(open(row.buyerId), { state: OPENED_FROM_LIST });
  }, [buyers.isFetching, navigate, open, rows]);

  if (detailPage) {
    return <Outlet context={{ self } satisfies BuyersOutletContext} />;
  }

  return (
    <>
      <PageHeader
        actions={
          canProvision || canExportCards ? (
            <>
              {canExportCards ? (
                <Link
                  className={buttonVariants({ className: OUTLINE_PILL, variant: 'outline' })}
                  to={physicalCardsPath('print')}
                >
                  <PrinterIcon aria-hidden="true" />
                  چاپ کارت
                </Link>
              ) : null}
              {canProvision && windowClass !== 'compact' ? (
                <Link
                  className={buttonVariants({ className: 'rounded-full px-5' })}
                  state={OPENED_FROM_LIST}
                  to="/buyers/new"
                >
                  <PlusIcon aria-hidden="true" />
                  خریدار جدید
                </Link>
              ) : null}
            </>
          ) : undefined
        }
        title="خریداران و اعتبار"
      />
      <SurfaceSection
        {...(totalEyebrow(total, 'خریدار') === undefined
          ? {}
          : { description: totalEyebrow(total, 'خریدار') })}
        id="buyer-list"
        title="خریداران ثبت‌شده"
      >
        <BuyerToolbar
          {...(summary.data?.buyerCountsByStatus === undefined
            ? {}
            : { counts: summary.data.buyerCountsByStatus })}
          filters={filters}
          onChange={change}
          onOpenSingle={tryOpenSingle}
        />
        <QueryState
          empty={
            <ListEmpty
              action={
                filtered ? (
                  <ClearFiltersButton onClick={() => change({}, true)} />
                ) : canProvision ? (
                  <Link className={buttonVariants()} state={OPENED_FROM_LIST} to="/buyers/new">
                    ثبت اولین خریدار
                  </Link>
                ) : undefined
              }
              title={filtered ? 'خریداری با این فیلترها پیدا نشد' : 'هنوز خریداری ثبت نشده است'}
            />
          }
          error={buyers.error}
          isEmpty={rows.length === 0}
          onRetry={() => void buyers.refetch()}
          skeleton={<ListSkeleton label="در حال بارگذاری خریداران…" />}
          status={listStatus(buyers)}
        >
          <AdminListTable
            caption="فهرست خریداران"
            columns={[
              {
                enableHiding: false,
                header: 'خریدار',
                id: 'buyer',
                pin: 'start',
                render: (row) => (
                  <span className="flex items-center gap-3">
                    <IconTile
                      icon={UserRoundIcon}
                      size="sm"
                      tone={BUYER_STATUS_TONE[row.status] ?? 'forest'}
                    />
                    <span className="saay-cell-main">
                      <Link className="font-bold" state={OPENED_FROM_LIST} to={open(row.buyerId)}>
                        {`${row.firstNameFa} ${row.lastNameFa}`}
                      </Link>
                      <small className="text-xs">
                        <Masked kind="mobile" value={row.mobileMasked} />
                      </small>
                    </span>
                  </span>
                ),
              },
              {
                header: 'کد ملی',
                id: 'nationalCode',
                render: (row) => <Masked kind="nationalCode" value={row.nationalCodeMasked} />,
              },
              {
                header: 'اعتبار آزاد',
                id: 'available',
                numeric: true,
                render: (row) => <Money rials={row.creditTotals.availableCreditIrr} />,
              },
              {
                header: 'وضعیت',
                id: 'status',
                render: (row) => <BuyerStatusBadge value={row.status} />,
              },
              {
                header: 'اعتبار کل',
                id: 'granted',
                numeric: true,
                render: (row) => <Money rials={row.creditTotals.currentGrantedCreditIrr} />,
              },
              {
                header: 'اصل بدهی',
                id: 'outstanding',
                numeric: true,
                render: (row) => <Money rials={row.creditTotals.outstandingPrincipalIrr} />,
              },
              {
                header: 'استان / شهر',
                id: 'geo',
                render: (row) => `${row.province.nameFa} / ${row.city.nameFa}`,
              },
              {
                header: 'تاریخ ثبت',
                id: 'created',
                render: (row) => formatTimestamp(row.createdAt, { withTime: false }),
              },
            ]}
            mobileSummary={(row) => ({
              primary: `${row.firstNameFa} ${row.lastNameFa}`,
              secondary: (
                <span className="admin-inline">
                  <BuyerStatusBadge value={row.status} />
                  <span>
                    {'اعتبار آزاد '}
                    <Money rials={row.creditTotals.availableCreditIrr} />
                  </span>
                </span>
              ),
            })}
            onRowClick={(row) => navigate(open(row.buyerId), { state: OPENED_FROM_LIST })}
            onVisibleColumnIdsChange={(ids) => {
              storeBuyersTableColumns(ids);
              setVisibleColumnIds(ids);
            }}
            rowActions={(row) => buyerRowActions(row, self, navigate, search)}
            rowKey={(row) => row.buyerId}
            rows={rows}
            visibleColumnIds={visibleColumnIds}
          />
          {buyers.isFetchNextPageError ? (
            <ErrorState error={buyers.error} onRetry={() => void buyers.fetchNextPage()} />
          ) : null}
          <ListFooter
            pager={
              <ListLoadMore
                hasMore={buyers.hasNextPage}
                loading={buyers.isFetchingNextPage}
                onLoadMore={() => void buyers.fetchNextPage()}
              />
            }
            shown={rows.length}
            total={total}
          />
        </QueryState>
      </SurfaceSection>
      <Outlet context={{ self } satisfies BuyersOutletContext} />
    </>
  );
}
